import { useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FolderLock, Plus, FileText, ListChecks, MessageSquare, Building2, ArrowRight } from "lucide-react";
import CreateDealDialog from "@/components/dataroom/CreateDealDialog";
import { STAGES, STAGE_STYLE, formatDate } from "@/lib/dataroom";

export default function Deals() {
  const { user } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const isExternal = user?.role === "external";

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals"],
    queryFn: async () => (await apiClient.get("/deals")).data,
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-ink text-brand-lime flex items-center justify-center shrink-0">
              <FolderLock className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-brand-ink tracking-tight">Datarom</h1>
              <p className="text-slate-500 mt-0.5">Sikre datarom for due diligence og transaksjoner</p>
            </div>
          </div>
          {!isExternal && (
            <Button onClick={() => setShowCreate(true)} data-testid="new-deal-button">
              <Plus className="w-4 h-4 mr-2" /> Nytt datarom
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => <div key={i} className="h-44 bg-white rounded-2xl animate-pulse" />)}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20">
            <FolderLock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-brand-ink mb-2">Ingen datarom ennå</h3>
            <p className="text-slate-500 mb-6">Opprett ditt første datarom for å samle dokumenter, sjekklister og Q&A.</p>
            {!isExternal && (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" /> Nytt datarom
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {deals.map((d) => (
              <Link
                key={d.id}
                to={`${createPageUrl("DataRoom")}?id=${d.id}`}
                data-testid={`deal-card-${d.id}`}
                className="group bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-bold text-brand-ink leading-snug group-hover:text-brand-limeDark transition-colors">{d.name}</h3>
                  <Badge className={`border-0 shrink-0 ${STAGE_STYLE[d.stage] || "bg-slate-100 text-slate-700"}`}>
                    {STAGES[d.stage] || d.stage}
                  </Badge>
                </div>
                {d.related_customer_name && (
                  <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> {d.related_customer_name}
                  </p>
                )}
                {d.description && <p className="mt-2 text-sm text-slate-500 line-clamp-2">{d.description}</p>}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {d.document_count || 0} dok.</span>
                  <span className="flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> {d.open_checklist || 0} åpne</span>
                  <span className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> {d.open_questions || 0} Q&A</span>
                  <ArrowRight className="w-4 h-4 ml-auto text-slate-300 group-hover:text-brand-limeDark transition-colors" />
                </div>
                <p className="mt-2 text-[11px] text-slate-400">Oppdatert {formatDate(d.updated_at)}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
      <CreateDealDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}
